package top.wcpe.allincore.ui.runtime.render

import top.wcpe.allincore.ui.runtime.material.MaterialInteractionState
import top.wcpe.allincore.ui.runtime.style.ScopedVisualModifier
import top.wcpe.allincore.ui.runtime.style.VisualModifier

/**
 * 视觉 modifier 链分派器（ADR-0041 / ADR-0046）。
 *
 * 按链顺序逐个 fold 视觉 modifier、分派到 [RenderSink]——**绘制顺序 = 链顺序**（叠放语义由作者控制），
 * 取代旧渲染器里硬编码的「阴影→背景→…→特效」固定序。`when` 对 [VisualModifier] sealed 穷举：
 * 加新效果只需补一个分支（开闭）。本体纯逻辑、零平台依赖，可单测。
 */
object ModifierDispatcher {

    private val EMPTY_SCOPE = AutoCloseable { }

    /**
     * 把 [modifiers] 按顺序分派到 [sink]。
     *
     * @param x 元素盒左上角 X（像素）
     * @param y 元素盒左上角 Y（像素）
     * @param w 元素盒宽（像素）
     * @param h 元素盒高（像素）
     */
    // when 对 VisualModifier sealed 穷举是 ADR-0041 的设计本身（加效果只补一个分支）——是平铺的查表式分派，
    // 非纠缠控制流；分支数随效果增长，故对本方法豁免 detekt LongMethod（与 LongParameterList 同款理由）。
    @Suppress("LongParameterList", "LongMethod")
    fun dispatch(
        modifiers: List<VisualModifier>,
        x: Float,
        y: Float,
        w: Float,
        h: Float,
        sink: RenderSink,
        states: Set<MaterialInteractionState> = emptySet(),
    ) {
        for (modifier in modifiers) {
            when (modifier) {
                is VisualModifier.Surface ->
                    sink.surface(
                        x,
                        y,
                        w,
                        h,
                        modifier.brush,
                        modifier.corner,
                        modifier.borderWidth,
                        modifier.borderColor,
                        modifier.mask,
                    )

                is VisualModifier.Shadow ->
                    sink.shadow(x, y, w, h, modifier.spec, modifier.corner)

                is VisualModifier.Neon ->
                    sink.neon(
                        x,
                        y,
                        w,
                        h,
                        modifier.color,
                        modifier.intensity,
                        modifier.size,
                        modifier.animated,
                        modifier.corner,
                    )

                is VisualModifier.Ripple ->
                    sink.ripple(
                        x,
                        y,
                        w,
                        h,
                        modifier.color,
                        modifier.progress,
                        modifier.radius,
                        modifier.centerX,
                        modifier.centerY,
                        modifier.corner,
                    )

                is VisualModifier.Flow ->
                    sink.flow(
                        x,
                        y,
                        w,
                        h,
                        modifier.color,
                        modifier.speedHz,
                        modifier.widthPx,
                        modifier.angleDeg,
                        modifier.mode,
                        modifier.corner,
                    )

                is VisualModifier.Energy ->
                    sink.energy(
                        x,
                        y,
                        w,
                        h,
                        modifier.color,
                        modifier.intensity,
                        modifier.frequency,
                        modifier.speed,
                        modifier.pattern,
                        modifier.corner,
                    )

                is VisualModifier.ProceduralTexture ->
                    sink.proceduralTexture(
                        x,
                        y,
                        w,
                        h,
                        modifier.kind,
                        modifier.colorA,
                        modifier.colorB,
                        modifier.scale,
                        modifier.density,
                        modifier.intensity,
                        modifier.corner,
                    )

                is VisualModifier.BackdropBlur ->
                    sink.backdropBlur(x, y, w, h, modifier.blurRadius)

                is VisualModifier.MaterialLayer ->
                    sink.materialLayer(x, y, w, h, modifier, states)

                // 粒子层（FR-UI-031 / ADR-0071）是独立段、由 UIRenderer 按前景 / 背景注入延迟绘制命令，
                // 正常不出现在 paint 链里；此分支仅为 sealed 穷举，留空即可（即便误入链也不重复绘制）。
                is VisualModifier.ParticleLayer -> Unit
            }
        }
    }

    /**
     * 把 [scoped] 作用域 modifier 以「begin 外→内 → [content] → end 内→外」包裹分派（ADR-0047）。
     *
     * 列表顺序即嵌套外→内：第一个最外层。begin 顺序遍历、end 逆序遍历，保证 push/pop 正确配对。
     * [content] 通常是「paint 链分派 + 元素内容 / 子树渲染」。
     *
     * @param x 元素盒左上角 X（像素）
     * @param y 元素盒左上角 Y（像素）
     * @param w 元素盒宽（像素）
     * @param h 元素盒高（像素）
     */
    @Suppress("LongParameterList")
    fun dispatchScoped(
        scoped: List<ScopedVisualModifier>,
        x: Float,
        y: Float,
        w: Float,
        h: Float,
        sink: RenderSink,
        content: () -> Unit,
    ) {
        val scope = beginScoped(scoped, x, y, w, h, sink)
        try {
            content()
        } finally {
            scope.close()
        }
    }

    /**
     * 打开作用域 modifier 并返回成对关闭句柄。
     *
     * 供存在早返回控制流的渲染器使用；调用方必须在 finally 中 close，语义等价于 [dispatchScoped]。
     */
    @Suppress("LongParameterList")
    fun beginScoped(
        scoped: List<ScopedVisualModifier>,
        x: Float,
        y: Float,
        w: Float,
        h: Float,
        sink: RenderSink,
    ): AutoCloseable {
        if (scoped.isEmpty()) return EMPTY_SCOPE
        val opened = ArrayList<ScopedVisualModifier>(scoped.size)
        for (modifier in scoped) {
            try {
                beginScopedModifier(modifier, x, y, w, h, sink)
                opened += modifier
            } catch (failure: RuntimeException) {
                closeScoped(opened, sink)
                throw failure
            } catch (failure: Error) {
                closeScoped(opened, sink)
                throw failure
            }
        }
        val activeScopes = opened.toList()
        return AutoCloseable {
            closeScoped(activeScopes, sink)
        }
    }

    private fun closeScoped(scoped: List<ScopedVisualModifier>, sink: RenderSink) {
        for (modifier in scoped.asReversed()) {
            endScopedModifier(modifier, sink)
        }
    }

    @Suppress("LongParameterList")
    private fun beginScopedModifier(
        modifier: ScopedVisualModifier,
        x: Float,
        y: Float,
        w: Float,
        h: Float,
        sink: RenderSink,
    ) {
        when (modifier) {
            is ScopedVisualModifier.Blur -> sink.beginBlur(x, y, w, h, modifier.blurRadius)
            is ScopedVisualModifier.BlendMode -> sink.pushBlendMode(modifier.mode)
            is ScopedVisualModifier.ColorFilter -> sink.pushColorFilter(modifier.matrix4x5)
            // elementId 由 UIRenderer 在分派前注入；缺失时传空串，平台层直通降级。
            is ScopedVisualModifier.History ->
                sink.beginHistoryCapture(x, y, w, h, modifier.elementId.orEmpty(), modifier.layer)
        }
    }

    private fun endScopedModifier(modifier: ScopedVisualModifier, sink: RenderSink) {
        when (modifier) {
            is ScopedVisualModifier.Blur -> sink.endBlur()
            is ScopedVisualModifier.BlendMode -> sink.popBlendMode()
            is ScopedVisualModifier.ColorFilter -> sink.popColorFilter()
            is ScopedVisualModifier.History -> sink.endHistoryCapture()
        }
    }
}
